import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Droplets, BookmarkPlus, Check } from "@/lib/icons";
import { playSplash } from "@/lib/labSounds";
import { useLabNotebook } from "@/lib/labNotebook";
import Titration from "./Titration";

interface Substance {
  id: string;
  name: string;
  formula: string;
  pH: number;
  type: "strong-acid" | "weak-acid" | "neutral" | "weak-base" | "strong-base";
}

const substances: Substance[] = [
  { id: "hcl", name: "Hydrochloric acid", formula: "HCl", pH: 1, type: "strong-acid" },
  { id: "h2so4", name: "Sulfuric acid", formula: "H₂SO₄", pH: 0.5, type: "strong-acid" },
  { id: "vinegar", name: "Vinegar", formula: "CH₃COOH", pH: 2.8, type: "weak-acid" },
  { id: "lemon", name: "Lemon juice", formula: "Citric acid", pH: 2.2, type: "weak-acid" },
  { id: "cola", name: "Cola", formula: "H₃PO₄ mix", pH: 3.0, type: "weak-acid" },
  { id: "coffee", name: "Black coffee", formula: "-", pH: 5.0, type: "weak-acid" },
  { id: "milk", name: "Milk", formula: "-", pH: 6.5, type: "weak-acid" },
  { id: "water", name: "Pure water", formula: "H₂O", pH: 7.0, type: "neutral" },
  { id: "blood", name: "Human blood", formula: "-", pH: 7.4, type: "weak-base" },
  { id: "baking-soda", name: "Baking soda solution", formula: "NaHCO₃", pH: 8.3, type: "weak-base" },
  { id: "ammonia", name: "Ammonia solution", formula: "NH₃", pH: 11.0, type: "weak-base" },
  { id: "bleach", name: "Bleach", formula: "NaOCl", pH: 12.5, type: "strong-base" },
  { id: "naoh", name: "Sodium hydroxide", formula: "NaOH", pH: 14, type: "strong-base" },
  { id: "ca-oh-2", name: "Lime water", formula: "Ca(OH)₂", pH: 12.4, type: "strong-base" },
];

function pHToColor(pH: number): string {
  // Universal indicator spectrum
  if (pH < 1) return "hsl(0, 85%, 45%)";
  if (pH < 2) return "hsl(0, 80%, 50%)";
  if (pH < 3) return "hsl(15, 85%, 50%)";
  if (pH < 4) return "hsl(30, 90%, 55%)";
  if (pH < 5) return "hsl(45, 90%, 50%)";
  if (pH < 6) return "hsl(55, 85%, 50%)";
  if (pH < 7) return "hsl(80, 60%, 50%)";
  if (pH < 8) return "hsl(120, 45%, 50%)";
  if (pH < 9) return "hsl(160, 50%, 45%)";
  if (pH < 10) return "hsl(190, 55%, 45%)";
  if (pH < 11) return "hsl(210, 60%, 50%)";
  if (pH < 12) return "hsl(230, 55%, 50%)";
  if (pH < 13) return "hsl(260, 50%, 45%)";
  return "hsl(280, 50%, 40%)";
}

function pHLabel(pH: number): string {
  if (pH < 3) return "Strongly Acidic";
  if (pH < 6) return "Weakly Acidic";
  if (pH < 8) return "Neutral";
  if (pH < 11) return "Weakly Basic";
  return "Strongly Basic";
}

export default function PHTest() {
  const [mode, setMode] = useState<"single" | "titration">("single");
  const [selected, setSelected] = useState<Substance | null>(null);
  const [logged, setLogged] = useState<string | null>(null);
  const { addEntry } = useLabNotebook();

  const handleSelect = (sub: Substance) => {
    setSelected(sub);
    setLogged(null);
    playSplash();
  };

  const logToNotebook = () => {
    if (!selected) return;
    addEntry({
      station: "ph",
      title: `pH test: ${selected.name}`,
      substance: `${selected.name} (${selected.formula})`,
      pH: selected.pH,
      observation: pHLabel(selected.pH),
    });
    setLogged(selected.id);
  };

  return (
    <div className="space-y-4">
      {/* Mode toggle */}
      <div className="flex items-center gap-1 p-1 rounded-lg bg-muted/50 border border-border w-fit" role="tablist" aria-label="pH mode">
        <button
          type="button"
          role="tab"
          aria-selected={mode === "single"}
          onClick={() => setMode("single")}
          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
            mode === "single" ? "bg-background text-foreground shadow-sm border border-border" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Single substance
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "titration"}
          onClick={() => setMode("titration")}
          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
            mode === "titration" ? "bg-background text-foreground shadow-sm border border-border" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Titration
        </button>
      </div>

      {mode === "titration" ? (
        <Titration />
      ) : (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
      {/* Substance selector */}
      <div className="rounded-xl border border-border bg-card p-4">
        <h3 className="text-sm font-medium text-foreground mb-1 flex items-center gap-2">
          <Droplets size={14} className="text-primary" />
          Substances
        </h3>
        <p className="text-[11px] text-muted-foreground mb-3">Select a substance to test its pH.</p>
        <div className="space-y-1 max-h-[500px] overflow-y-auto pr-1 -mr-1">
          {substances.map((sub) => (
            <button
              key={sub.id}
              onClick={() => handleSelect(sub)}
              className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg border transition-all text-left ${
                selected?.id === sub.id
                  ? "border-primary bg-primary/5"
                  : "border-border bg-card hover:bg-accent/50 hover:border-primary/30"
              }`}
            >
              <div
                className="w-4 h-4 rounded-full border border-border/50 flex-shrink-0"
                style={{ backgroundColor: pHToColor(sub.pH) }}
              />
              <div className="flex-1 min-w-0">
                <span className="text-xs font-medium text-foreground">{sub.name}</span>
                <span className="text-[10px] text-muted-foreground block truncate font-mono">{sub.formula}</span>
              </div>
              <span className="text-[10px] font-mono text-muted-foreground flex-shrink-0">pH {sub.pH}</span>
            </button>
          ))}
        </div>
      </div>

      {/* pH visualization */}
      <div className="rounded-xl border border-border bg-gradient-to-b from-card to-muted/30 p-6 flex flex-col items-center gap-6 min-h-[450px]">
        {/* Test tube with indicator */}
        <div className="flex items-end gap-8">
          <div className="relative w-20 h-56 flex flex-col items-center justify-end">
            {/* Tube */}
            <svg viewBox="0 0 60 200" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
              {/* Tube outline */}
              <path
                d="M 15 10 L 15 160 Q 15 190 30 190 Q 45 190 45 160 L 45 10"
                fill="none"
                stroke="hsl(var(--border))"
                strokeWidth="2"
              />
              {/* Rim */}
              <path d="M 12 10 L 15 10 M 45 10 L 48 10" stroke="hsl(var(--border))" strokeWidth="2.5" />
              <ellipse cx="30" cy="10" rx="16.5" ry="3" fill="none" stroke="hsl(var(--border))" strokeWidth="1.5" />

              {/* Liquid */}
              <AnimatePresence>
                {selected && (
                  <motion.g>
                    <defs>
                      <clipPath id="tubeClip">
                        <path d="M 17 10 L 17 160 Q 17 188 30 188 Q 43 188 43 160 L 43 10 Z" />
                      </clipPath>
                    </defs>
                    <motion.rect
                      x="17"
                      width="26"
                      clipPath="url(#tubeClip)"
                      fill={pHToColor(selected.pH)}
                      opacity={0.65}
                      initial={{ y: 190, height: 0 }}
                      animate={{ y: 60, height: 130 }}
                      transition={{ type: "spring", stiffness: 50, damping: 12 }}
                    />
                  </motion.g>
                )}
              </AnimatePresence>
            </svg>
          </div>

          {/* pH meter display */}
          <AnimatePresence mode="wait">
            {selected && (
              <motion.div
                key={selected.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="rounded-xl border border-border bg-card p-5 shadow-lg min-w-[180px]"
              >
                <p className="text-xs text-muted-foreground mb-1">pH Value</p>
                <motion.p
                  className="text-5xl font-mono font-bold"
                  style={{ color: pHToColor(selected.pH) }}
                  initial={{ scale: 0.5 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200 }}
                >
                  {selected.pH.toFixed(1)}
                </motion.p>
                <p className="text-sm font-medium text-foreground mt-1">{pHLabel(selected.pH)}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{selected.name}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* pH Scale */}
        <div className="w-full max-w-md">
          <div className="relative h-8 rounded-full overflow-hidden border border-border">
            <div
              className="absolute inset-0"
              style={{
                background: "linear-gradient(to right, hsl(0,85%,45%), hsl(15,85%,50%), hsl(30,90%,55%), hsl(45,90%,50%), hsl(55,85%,50%), hsl(80,60%,50%), hsl(120,45%,50%), hsl(160,50%,45%), hsl(190,55%,45%), hsl(210,60%,50%), hsl(230,55%,50%), hsl(260,50%,45%), hsl(280,50%,40%))",
              }}
            />
            {/* Marker */}
            <AnimatePresence>
              {selected && (
                <motion.div
                  className="absolute top-0 bottom-0 w-1 bg-foreground rounded-full shadow-lg"
                  style={{ left: `${(selected.pH / 14) * 100}%` }}
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: 1 }}
                  transition={{ type: "spring", stiffness: 200 }}
                />
              )}
            </AnimatePresence>
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-[10px] text-muted-foreground">0 (Acid)</span>
            <span className="text-[10px] text-muted-foreground">7 (Neutral)</span>
            <span className="text-[10px] text-muted-foreground">14 (Base)</span>
          </div>
        </div>

        {!selected && (
          <p className="text-sm text-muted-foreground">Select a substance to test its pH</p>
        )}

        {selected && (
          <button
            onClick={logToNotebook}
            disabled={logged === selected.id}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 disabled:opacity-60 disabled:cursor-default transition-colors"
            aria-label={logged === selected.id ? "Saved to notebook" : "Save reading to notebook"}
          >
            {logged === selected.id ? <Check size={12} aria-hidden="true" /> : <BookmarkPlus size={12} aria-hidden="true" />}
            {logged === selected.id ? "Saved to notebook" : "Save to notebook"}
          </button>
        )}
      </div>
    </div>
      )}
    </div>
  );
}
